import type { ChatIntentScope, ChatIntentStorage } from './chatIntentJournal'

const STORAGE_SCHEMA = 'animus.arena.mutation-intents.v1'
const STORAGE_PREFIX = STORAGE_SCHEMA
const DEFAULT_TTL_MS = 24 * 60 * 60 * 1_000
const DEFAULT_CAPACITY = 32
const DEFAULT_MAX_BYTES = 32 * 1_024

export type MutationIntentKind =
  | 'subject_launch'
  | 'interaction_response'
  | 'agent_chat_start'

export interface PendingMutationIntent {
  bindingHash: string
  requestHash: string
  idempotencyKey: string
  createdAt: number
  expiresAt: number
}

interface StoredEnvelope {
  schema: typeof STORAGE_SCHEMA
  intents: PendingMutationIntent[]
}

class MemoryMutationIntentStorage implements ChatIntentStorage {
  private readonly values = new Map<string, string>()
  getItem(key: string): string | null { return this.values.get(key) ?? null }
  setItem(key: string, value: string): void { this.values.set(key, value) }
  removeItem(key: string): void { this.values.delete(key) }
}

function boundedIdentifier(value: unknown, maximum = 512): value is string {
  return typeof value === 'string' &&
    value.length > 0 &&
    value.length <= maximum &&
    value === value.trim() &&
    !/[\u0000-\u001f\u007f]/.test(value)
}

function validScope(scope: ChatIntentScope): boolean {
  return boundedIdentifier(scope.userId) &&
    boundedIdentifier(scope.workspaceId) &&
    boundedIdentifier(scope.projectId) &&
    boundedIdentifier(scope.sessionBinding)
}

function hash(value: unknown): value is string {
  return typeof value === 'string' && /^[a-f0-9]{64}$/.test(value)
}

async function sha256(value: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value))
  return [...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')
}

function storedIntent(value: unknown): PendingMutationIntent | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const source = value as Record<string, unknown>
  if (
    !hash(source.bindingHash) ||
    !hash(source.requestHash) ||
    typeof source.idempotencyKey !== 'string' ||
    !/^[A-Za-z0-9._:-]{16,200}$/.test(source.idempotencyKey) ||
    !Number.isSafeInteger(source.createdAt) ||
    !Number.isSafeInteger(source.expiresAt) ||
    Number(source.expiresAt) <= Number(source.createdAt)
  ) return null
  return {
    bindingHash: source.bindingHash,
    requestHash: source.requestHash,
    idempotencyKey: source.idempotencyKey,
    createdAt: Number(source.createdAt),
    expiresAt: Number(source.expiresAt),
  }
}

/**
 * Reload-safe journal for launch, interaction, and agent-chat mutations with
 * uncertain outcomes.
 * It stores only session-partitioned hashes and idempotency keys—never answer
 * text, workflow input, credentials, user ids, or Portal resource ids.
 */
export class MutationIntentJournal {
  private readonly storage: ChatIntentStorage
  private readonly now: () => number
  private readonly ttlMs: number
  private readonly capacity: number
  private readonly maxBytes: number
  private readonly scopeKey: Promise<string>

  constructor(private readonly options: {
    scope: ChatIntentScope
    storage?: ChatIntentStorage
    now?: () => number
    ttlMs?: number
    capacity?: number
    maxBytes?: number
    pruneForeignPartitions?: boolean
  }) {
    if (!validScope(options.scope)) throw new Error('Invalid mutation intent scope')
    this.storage = options.storage ?? new MemoryMutationIntentStorage()
    this.now = options.now ?? Date.now
    this.ttlMs = options.ttlMs ?? DEFAULT_TTL_MS
    this.capacity = options.capacity ?? DEFAULT_CAPACITY
    this.maxBytes = options.maxBytes ?? DEFAULT_MAX_BYTES
    if (!Number.isSafeInteger(this.ttlMs) || this.ttlMs < 1 || this.ttlMs > 7 * DEFAULT_TTL_MS) {
      throw new Error('Invalid mutation intent TTL')
    }
    if (!Number.isSafeInteger(this.capacity) || this.capacity < 1 || this.capacity > 128) {
      throw new Error('Invalid mutation intent capacity')
    }
    if (!Number.isSafeInteger(this.maxBytes) || this.maxBytes < 1_024 || this.maxBytes > 128 * 1_024) {
      throw new Error('Invalid mutation intent storage bound')
    }
    this.scopeKey = sha256(JSON.stringify([
      STORAGE_SCHEMA,
      options.scope.userId,
      options.scope.workspaceId,
      options.scope.projectId,
      options.scope.sessionBinding,
    ])).then((digest) => `${STORAGE_PREFIX}:${digest}`)
      .then((key) => {
        if (options.pruneForeignPartitions) this.pruneForeignPartitions(key)
        return key
      })
  }

  async claim(
    kind: MutationIntentKind,
    binding: string,
    normalizedBody: string,
  ): Promise<PendingMutationIntent> {
    if (!boundedIdentifier(binding, 1_024) || normalizedBody.length > 32 * 1_024) {
      throw new Error('Invalid mutation intent binding')
    }
    const scope = this.options.scope
    const bindingHash = await sha256(JSON.stringify({
      schema: 'animus.arena.mutation-binding.v1',
      kind,
      user_id: scope.userId,
      workspace_id: scope.workspaceId,
      project_id: scope.projectId,
      session_binding: scope.sessionBinding,
      binding,
    }))
    const requestHash = await sha256(JSON.stringify({
      schema: 'animus.arena.mutation-request.v1',
      binding_hash: bindingHash,
      body: normalizedBody,
    }))
    const intents = await this.load()
    const existing = intents.find((intent) => intent.bindingHash === bindingHash)
    if (existing) {
      if (existing.requestHash !== requestHash) {
        throw new Error('Pending mutation intent does not match the uncertain request')
      }
      return existing
    }
    if (intents.length >= this.capacity) throw new Error('Pending mutation intent capacity exceeded')
    const now = this.now()
    const created: PendingMutationIntent = {
      bindingHash,
      requestHash,
      idempotencyKey: crypto.randomUUID(),
      createdAt: now,
      expiresAt: now + this.ttlMs,
    }
    await this.save([...intents, created])
    return created
  }

  async clear(intent: PendingMutationIntent): Promise<void> {
    const intents = await this.load()
    await this.save(intents.filter((candidate) => !(
      candidate.bindingHash === intent.bindingHash &&
      candidate.requestHash === intent.requestHash &&
      candidate.idempotencyKey === intent.idempotencyKey
    )))
  }

  private async load(): Promise<PendingMutationIntent[]> {
    const key = await this.scopeKey
    let raw: string | null
    try {
      raw = this.storage.getItem(key)
    } catch {
      throw new Error('Mutation intent persistence is unavailable')
    }
    if (!raw) return []
    if (new TextEncoder().encode(raw).byteLength > this.maxBytes) {
      this.remove(key)
      return []
    }
    let decoded: unknown
    try {
      decoded = JSON.parse(raw) as unknown
    } catch {
      this.remove(key)
      return []
    }
    if (!decoded || typeof decoded !== 'object' || Array.isArray(decoded)) {
      this.remove(key)
      return []
    }
    const envelope = decoded as Partial<StoredEnvelope>
    if (envelope.schema !== STORAGE_SCHEMA || !Array.isArray(envelope.intents)) {
      this.remove(key)
      return []
    }
    const now = this.now()
    const valid = envelope.intents
      .slice(0, this.capacity + 1)
      .map(storedIntent)
      .filter((intent): intent is PendingMutationIntent => intent !== null && intent.expiresAt > now)
    if (valid.length > this.capacity) {
      this.remove(key)
      return []
    }
    if (valid.length !== envelope.intents.length) await this.save(valid)
    return valid
  }

  private async save(intents: PendingMutationIntent[]): Promise<void> {
    const key = await this.scopeKey
    if (intents.length === 0) {
      this.remove(key)
      return
    }
    const value = JSON.stringify({ schema: STORAGE_SCHEMA, intents } satisfies StoredEnvelope)
    if (new TextEncoder().encode(value).byteLength > this.maxBytes) {
      throw new Error('Pending mutation intent storage bound exceeded')
    }
    try {
      this.storage.setItem(key, value)
    } catch {
      throw new Error('Mutation intent persistence is unavailable')
    }
  }

  private remove(key: string): void {
    try {
      this.storage.removeItem(key)
    } catch {
      throw new Error('Mutation intent persistence is unavailable')
    }
  }

  private pruneForeignPartitions(currentKey: string): void {
    if (typeof this.storage.length !== 'number' || typeof this.storage.key !== 'function') return
    try {
      const stale: string[] = []
      for (let index = 0; index < this.storage.length; index += 1) {
        const key = this.storage.key(index)
        if (key?.startsWith(`${STORAGE_PREFIX}:`) && key !== currentKey) stale.push(key)
      }
      for (const key of stale) this.storage.removeItem(key)
    } catch {
      throw new Error('Mutation intent persistence is unavailable')
    }
  }
}
